import { useCallback, useMemo } from 'react';
import {
    hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { useOpenModal } from '@rainbow-modules/hooks';
import ModalTitle from 'components/ModalTitle';
import { Memory } from 'data/firestore/agent/customer/memory/types';
import useHttpMutation from 'data/firestore/useHttpMutation';

const mapDataToFormValues = (data: Memory) => data;

const useManageCustomerMemory = ({
    agentId,
    customerId,
    modalId,
    onMemoryUpdated = () => {},
} : {
    agentId: string;
    customerId?: string | null;
    modalId: string;
    onMemoryUpdated?: () => void;
}) => {
    const [openForm, closeForm] = useOpenModal(modalId);

    const { mutateAsync: updateMemory } = useHttpMutation({
        method: 'PATCH',
        onSuccess: () => {
            closeForm();
            onMemoryUpdated();
        },
    });

    const handleEditTag = useCallback(
        (data: Memory) => openForm({
            id: data?.id,
            agentId,
            title: <ModalTitle>Edit Customer Memory</ModalTitle>,
            submitButtonLabel: 'Update',
            initialValues: mapDataToFormValues(data),
            onSubmit: async (values: Record<string, unknown>) => {
                if (!customerId) return;
                const { content, importance } = values;
                showAppSpinner();
                try {
                    await updateMemory({
                        pathname: `/agents/${agentId}/customers/${customerId}/memories/${data.id}`,
                        body: {
                            content,
                            importance,
                        },
                    });
                    showAppNotification({
                        title: 'Success',
                        description: 'Memory updated successfully.',
                        icon: 'success',
                        timeout: 5000,
                    });
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [openForm, agentId, updateMemory, customerId],
    );

    return useMemo(() => ({
        edit: handleEditTag,
        closeForm,
    }), [closeForm, handleEditTag]);
};

export default useManageCustomerMemory;
